import { Fragment, useMemo, useState } from "react";
import { Trans, useLingui } from "@lingui/react/macro";
import {
  ActionIcon,
  Divider,
  Drawer,
  Flex,
  Grid,
  Group,
  HoverCard,
  LoadingOverlay,
  Paper,
  Select,
  Stack,
  Switch,
  Text,
  Textarea,
  TextInput,
  Tooltip,
} from "@mantine/core";
import { TimeInput } from "@mantine/dates";
import { useForm, zodResolver } from "@mantine/form";
import { useDebouncedCallback, useDisclosure } from "@mantine/hooks";
import { IconExternalLink, IconUser } from "@tabler/icons-react";
import { useQueryClient } from "@tanstack/react-query";
import { Link, useBlocker } from "@tanstack/react-router";
import dayjs from "dayjs";
import { sortBy } from "lodash-es";
import {
  createMRTColumnHelper,
  MantineReactTable,
  MRT_GlobalFilterTextInput,
  MRT_ShowHideColumnsButton,
  MRT_ToggleFiltersButton,
  MRT_ToggleFullScreenButton,
  useMantineReactTable,
} from "mantine-react-table";
import { z } from "zod";

import UpdateAdditionalInformationModal from "@/features/bookings/update-booking/update-additional-information-modal";
import { bookingStatusLabels } from "@/features/bookings/utils/status";
import { bookingVisitCategoryLabels } from "@/features/bookings/utils/visit-category";
import {
  getGetBookingQueryKey,
  getGetBookingsQueryKey,
  useGetBookings,
  useUpdateBookingVisitInformation,
} from "@/shared/api/generated/booking";
import {
  useGetCustomerbyCustomerNumber,
  useGetCustomers,
} from "@/shared/api/generated/customers";
import { useGetDepartmentsOptionallyfilteredbylocation } from "@/shared/api/generated/departments";
import {
  GetBookingData,
  GetBookingDataPaymentCategory,
  GetBookingDataPaymentCategoryDrinks,
  GetBookingDataVisitCategory,
  GetBookingResponse,
  GetBookingsItem,
  GetBookingsResponse,
} from "@/shared/api/generated/schemas";
import { AnchorLink } from "@/shared/components/AnchorLink";
import { MaskedDateInput } from "@/shared/components/MaskedDateInput";
import { getDefaultMRTOptions } from "@/shared/components/table/defaultMRTOptions";
import {
  GetFieldsBySubjectKind,
  getSubject,
} from "@/shared/providers/casl-provider/authorization/subjects";
import { useAppAbility } from "@/shared/providers/casl-provider/use-app-ability";
import { countryListnbNO } from "@/shared/utils/countryList";
import { emptyStringToNull } from "@/shared/utils/forms/emptyStringToNull";
import { nullableInput } from "@/shared/utils/forms/nullableInput";

import { bookingPaymentFormOptions } from "../utils/payment-form";
import { bookingPaymentFormDrinksOptions } from "../utils/payment-form-drinks";
import { bookingVisitCategoryOptions } from "../utils/visit-category";

function useVisitInformationBookingFormSchema() {
  const { t } = useLingui();

  return z
    .object({
      date: nullableInput(
        z.date({ message: t`Velg en dato` }),
        t`Velg en dato`,
      ),
      timeStart: z.string(),
      timeEnd: z.string(),
      customer: nullableInput(
        z.string().min(1, t`Velg en kunde`),
        t`Velg en kunde`,
      ),
      department: z.string().nullable(),
      visitCategory: z
        .nativeEnum(GetBookingDataVisitCategory)
        .nullable()
        .optional(),
      paymentForm: z
        .nativeEnum(GetBookingDataPaymentCategory)
        .nullable()
        .optional(),
      paymentFormDrinks: z
        .nativeEnum(GetBookingDataPaymentCategoryDrinks)
        .nullable()
        .optional(),
    })
    .refine(
      (data) => {
        const start = dayjs(data.timeStart, "HH:mm");
        const end = dayjs(data.timeEnd, "HH:mm");

        if (start.isValid() && end.isValid()) {
          return end.isAfter(start);
        }
        return true;
      },
      {
        message: t`Slutt må være etter start`,
        path: ["timeEnd"],
      },
    );
}

type VisitInformationBookingFormInitial = z.input<
  ReturnType<typeof useVisitInformationBookingFormSchema>
>;
type VisitInformationBookingFormSubmit = z.output<
  ReturnType<typeof useVisitInformationBookingFormSchema>
>;

export default function VisitInformation({
  booking,
}: {
  booking: GetBookingData;
}) {
  const { i18n, t } = useLingui();
  const [drawerOpened, { open: openDrawer, close: closeDrawer }] =
    useDisclosure(false);

  const { data: customers } = useGetCustomers();
  const { data: departments } = useGetDepartmentsOptionallyfilteredbylocation({
    location: booking.location.id,
  });

  const debouncedSubmit = useDebouncedCallback(() => {
    form.onSubmit(handleSubmit)();
  }, 1000);

  const visitInformationBookingFormSchema =
    useVisitInformationBookingFormSchema();

  const form = useForm<
    VisitInformationBookingFormInitial,
    () => VisitInformationBookingFormSubmit
  >({
    mode: "controlled",
    validate: zodResolver(visitInformationBookingFormSchema),
    initialValues: {
      date: dayjs(booking.arrival).toDate(),
      timeStart: dayjs(booking.arrival).format("HH:mm"),
      timeEnd: dayjs(booking.departure).format("HH:mm"),
      customer: booking.customer.id.toString(),
      department: booking.department?.id ?? null,
      visitCategory: emptyStringToNull(booking.visitCategory ?? ""),
      paymentForm: emptyStringToNull(booking.paymentCategory ?? ""),
      paymentFormDrinks: emptyStringToNull(booking.paymentCategoryDrinks ?? ""),
    },
    onValuesChange: debouncedSubmit,
  });

  const selectedCustomerId = form.getValues().customer;

  useBlocker({
    blockerFn: () =>
      window.confirm(
        t`Det er gjort endringer i skjemaet som ikke er lagret. Trykk "OK" for å forkaste endringene.`,
      ),
    condition: form.isDirty(),
  });

  const queryClient = useQueryClient();

  const { mutateAsync: updateBookingVisitInformation } =
    useUpdateBookingVisitInformation({
      mutation: {
        onSuccess: (data) => {
          queryClient.setQueryData<GetBookingResponse>(
            getGetBookingQueryKey(booking.id),
            (prev) => ({
              ...prev,
              data: { ...prev?.data, ...data.data },
            }),
          );
          // Update this booking in the for GET /bookings
          queryClient.setQueriesData<GetBookingsResponse>(
            { queryKey: getGetBookingsQueryKey() },
            (old) => ({
              ...old,
              data: (old?.data ?? []).map((booking) =>
                booking.id === data.data.id ? data.data : booking,
              ),
            }),
          );
        },
      },
    });

  async function handleSubmit(values: VisitInformationBookingFormSubmit) {
    let arrival = dayjs(values.date).startOf("day");
    let departure = dayjs(values.date).endOf("day");

    if (values.timeStart) {
      const startTime = dayjs(values.timeStart, "HH:mm");
      arrival = dayjs(values.date)
        .hour(startTime.hour())
        .minute(startTime.minute());
    }

    if (values.timeEnd) {
      const endTime = dayjs(values.timeEnd, "HH:mm");
      departure = dayjs(values.date)
        .hour(endTime.hour())
        .minute(endTime.minute());
    }

    await updateBookingVisitInformation({
      id: booking.id,
      data: {
        locationId: booking.location.id,
        customerId: parseInt(values.customer),
        arrival: arrival.toISOString(),
        departure: departure.toISOString(),
        visitCategory: values.visitCategory,
        paymentCategory: values.paymentForm,
        paymentCategoryDrinks: values.paymentFormDrinks,
        departmentId: values.department,
        updatedAt: booking.updatedAt,
      },
    });
    form.resetDirty(values);
  }

  const ability = useAppAbility();

  const canUpdateBooking = (field: GetFieldsBySubjectKind<"Booking">) =>
    ability.can("update", getSubject("Booking", booking), field);

  return (
    <Paper p="xs" h={"100%"}>
      <Text size="sm" mb="sm">
        <Trans>Besøksinformasjon</Trans>
      </Text>

      <Stack gap="sm">
        <Group grow align="flex-start">
          <MaskedDateInput
            label={t`Ankomstdato`}
            withAsterisk
            clearable
            {...form.getInputProps("date")}
            key={form.key("date")}
            readOnly={!canUpdateBooking("arrival")}
          />

          <Group grow align="flex-start">
            <TimeInput
              label={t`Ankomst`}
              {...form.getInputProps("timeStart")}
              key={form.key("timeStart")}
              readOnly={!canUpdateBooking("arrival")}
            />
            <TimeInput
              label={t`Avreise`}
              {...form.getInputProps("timeEnd")}
              key={form.key("timeEnd")}
              readOnly={!canUpdateBooking("departure")}
            />
          </Group>
        </Group>

        <Group grow align="flex-start">
          <Select
            label={t`Avdeling`}
            placeholder={t`Velg avdeling`}
            data={sortBy(
              departments?.data?.map((department) => ({
                value: department.id,
                label: department.name,
              })),
              (o) => o.label.toLowerCase(),
            )}
            searchable
            clearable
            {...form.getInputProps("department")}
            key={form.key("department")}
            readOnly={!canUpdateBooking("department")}
          />
        </Group>

        <Group grow align="flex-start">
          <Group gap="sm" align="end">
            <Select
              flex={1}
              placeholder={t`Velg kunde`}
              data={sortBy(
                customers?.data?.map((customer) => ({
                  value: customer.id.toString(),
                  label: customer.name,
                })),
                (o) => o.label.toLowerCase(),
              )}
              searchable
              clearable
              withAsterisk
              label={t`Kunde`}
              {...form.getInputProps("customer")}
              key={form.key("customer")}
              readOnly={!canUpdateBooking("customer")}
            />
            <Tooltip label={t`Vis kundedetaljer`}>
              <ActionIcon
                variant="light"
                size="input-sm"
                onClick={openDrawer}
                disabled={!selectedCustomerId}
              >
                <IconUser />
              </ActionIcon>
            </Tooltip>
          </Group>
        </Group>
        <Select
          label={t`Besøkskategori`}
          placeholder={t`Velg besøkskategori`}
          data={sortBy(bookingVisitCategoryOptions(i18n), (o) =>
            o.label.toLowerCase(),
          )}
          searchable
          clearable
          {...form.getInputProps("visitCategory")}
          key={form.key("visitCategory")}
          readOnly={!canUpdateBooking("visitCategory")}
        />

        <Group grow align="flex-start">
          <Select
            label={t`Betalingsform`}
            placeholder={t`Velg betalingsform`}
            data={sortBy(bookingPaymentFormOptions(i18n), (o) =>
              o.label.toLowerCase(),
            )}
            searchable
            clearable
            {...form.getInputProps("paymentForm")}
            key={form.key("paymentForm")}
            readOnly={!canUpdateBooking("paymentCategory")}
          />
        </Group>
        <Group grow align="flex-start">
          <Select
            label={t`Betalingsform drikke`}
            placeholder={t`Velg betalingsform drikke`}
            data={sortBy(bookingPaymentFormDrinksOptions(i18n), (o) =>
              o.label.toLowerCase(),
            )}
            searchable
            clearable
            {...form.getInputProps("paymentFormDrinks")}
            key={form.key("paymentFormDrinks")}
            readOnly={!canUpdateBooking("paymentCategoryDrinks")}
          />
        </Group>
      </Stack>

      <Drawer
        opened={drawerOpened}
        onClose={closeDrawer}
        title={
          <Group gap="sm">
            <Trans>Kundedetaljer</Trans>
            {selectedCustomerId && (
              <Link
                to="/museum/$museumId/customers/$customerId"
                params={{
                  museumId: booking.location.id,
                  customerId: selectedCustomerId,
                }}
                target="_blank"
              >
                <ActionIcon variant="subtle" size="sm">
                  <IconExternalLink />
                </ActionIcon>
              </Link>
            )}
          </Group>
        }
        position="right"
        size="xl"
      >
        <CustomerDetailsDrawerContent
          selectedCustomerId={selectedCustomerId}
          locationId={booking.location.id}
        />
      </Drawer>
    </Paper>
  );
}

function CustomerDetailsDrawerContent({
  selectedCustomerId,
  locationId,
}: {
  selectedCustomerId: string | null;
  locationId: string;
}) {
  const { data: customerResponse, isLoading: isLoadingCustomer } =
    useGetCustomerbyCustomerNumber(
      selectedCustomerId ? parseInt(selectedCustomerId) : 0,
      {
        query: {
          enabled: !!selectedCustomerId,
        },
      },
    );

  const { t } = useLingui();

  const isInvoiceAddressTheSameAsAddress =
    customerResponse?.data?.invoiceAddressLine1 ===
      customerResponse?.data?.addressLine1 &&
    customerResponse?.data?.invoicePostalCode ===
      customerResponse?.data?.postalCode &&
    customerResponse?.data?.invoiceLocality ===
      customerResponse?.data?.locality;

  if (isLoadingCustomer) {
    return <LoadingOverlay visible={true} />;
  }

  if (!customerResponse?.data) {
    return <Text c="dimmed">{t`Kunne ikke laste kundedetaljer`}</Text>;
  }

  const customerData = customerResponse.data;

  return (
    <Stack gap="lg">
      <Grid>
        <Grid.Col span={12}>
          <Paper p="xs">
            <Text size="sm" mb="sm" fw={500}>
              <Trans>Detaljer og kontaktinformasjon</Trans>
            </Text>

            <Stack gap="sm">
              <Group grow>
                <TextInput label={t`Navn`} value={customerData.name} readOnly />
                <TextInput
                  label={t`PowerOffice ID`}
                  value={customerData.powerofficeCustomerId ?? ""}
                  readOnly
                />
              </Group>

              <Group grow align="start">
                <Select
                  label={t`Land`}
                  data={countryListnbNO}
                  value={customerData.countryCode}
                  readOnly
                />
                <TextInput
                  label={t`Orgnum./EHF`}
                  value={customerData.organizationNumber ?? ""}
                  readOnly
                />
              </Group>
              <Group grow align="start">
                <TextInput
                  label={t`Telefon`}
                  value={customerData.phoneNumber ?? ""}
                  readOnly
                />
                <TextInput
                  label={t`Epost`}
                  value={customerData.email ?? ""}
                  readOnly
                />
              </Group>
              <Textarea
                label={t`Extra informasjon`}
                value={customerData.information ?? ""}
                readOnly
                autosize
                minRows={3}
              />
            </Stack>
          </Paper>
        </Grid.Col>

        <Grid.Col span={12}>
          <Paper p="xs">
            <Text size="sm" mb="sm" fw={500}>
              <Trans>Gate- og fakturaadresse</Trans>
            </Text>

            <Stack gap="sm">
              <Group grow>
                <TextInput
                  label={t`Gateadresse`}
                  value={customerData.addressLine1 ?? ""}
                  readOnly
                />
              </Group>
              <Group grow>
                <TextInput
                  label={t`Postnummer`}
                  value={customerData.postalCode ?? ""}
                  readOnly
                />
                <TextInput
                  label={t`Sted`}
                  value={customerData.locality ?? ""}
                  readOnly
                />
              </Group>
              {!isInvoiceAddressTheSameAsAddress && (
                <>
                  <TextInput
                    label={t`Fakturaadresse`}
                    value={customerData.invoiceAddressLine1 ?? ""}
                    readOnly
                  />
                  <Group grow>
                    <TextInput
                      label={t`Postnummer`}
                      value={customerData.invoicePostalCode ?? ""}
                      readOnly
                    />
                    <TextInput
                      label={t`Sted`}
                      value={customerData.invoiceLocality ?? ""}
                      readOnly
                    />
                  </Group>
                </>
              )}
            </Stack>
          </Paper>
        </Grid.Col>

        {customerData.contactPerson &&
          customerData.contactPerson.length > 0 && (
            <Grid.Col span={12}>
              <Paper p="xs">
                <Text size="sm" mb="sm" fw={500}>
                  <Trans>Kontaktpersoner</Trans>
                </Text>

                <Stack gap="xs">
                  {customerData.contactPerson.map((person, index) => (
                    <TextInput key={index} value={person} readOnly />
                  ))}
                </Stack>
              </Paper>
            </Grid.Col>
          )}
      </Grid>

      <CustomerBookings
        customerId={selectedCustomerId}
        locationId={locationId}
      />
    </Stack>
  );
}

function CustomerBookings({
  customerId,
  locationId,
}: {
  customerId: string | null;
  locationId: string;
}) {
  const { i18n, t } = useLingui();
  const [includeCancelled, setIncludeCancelled] = useState(false);

  const { data, isLoading } = useGetBookings({
    includeProgram: true,
    includeCancelled: includeCancelled,
    customers: customerId ? [customerId] : undefined,
    locations: [locationId],
  });

  const ch = createMRTColumnHelper<GetBookingsItem>();

  const columns = [
    ch.accessor("id", {
      header: "#",
      Cell: ({ cell, renderedCellValue }) => (
        <AnchorLink
          to="/museum/$museumId/bookings/$bookingId"
          params={{
            bookingId: cell.getValue().toString(),
            museumId: locationId,
          }}
        >
          {renderedCellValue}
        </AnchorLink>
      ),
      size: 90,
    }),
    ch.accessor(
      (row) =>
        i18n.date(row.arrival, { dateStyle: "short", timeStyle: "short" }),
      {
        id: "arrival",
        header: t`Ankomst`,
        filterVariant: "date-range",
        filterFn: (row, _columnId, filterValue) => {
          const [start, end] = filterValue as [
            Date | undefined | "",
            Date | undefined | "",
          ];

          // not set - Date
          if (!start && end) {
            return dayjs(row.original.arrival).isSameOrBefore(end, "day");
          }

          // Date - not set
          if (start && !end) {
            return dayjs(row.original.arrival).isSameOrAfter(start, "day");
          }

          // Date - Date
          if (start && end) {
            return dayjs(row.original.arrival).isBetween(
              start,
              end,
              "day",
              "[]",
            );
          }

          return true;
        },
      },
    ),
    ch.accessor("group.guests.booked", {
      header: t`Antall`,
      filterVariant: "range",
      size: 120,
    }),
    ch.accessor(
      (row) =>
        row.visitCategory
          ? i18n.t(bookingVisitCategoryLabels[row.visitCategory]) ||
            row.visitCategory
          : "-",
      {
        header: t`Type`,
        filterVariant: "select",
      },
    ),
    ch.accessor("program.id", {
      header: t`Program`,
      Cell: ({ row }) => <ProgramCell program={row.original.program} />,
      enableColumnFilter: false,
      minSize: 300,
    }),
    ch.accessor(
      (row) =>
        row.status
          ? i18n.t(bookingStatusLabels[row.status]) || row.status
          : "-",
      {
        header: t`Status`,
        filterVariant: "multi-select",
      },
    ),
    ch.accessor(
      (row) =>
        row.guide ? `${row.guide.firstName} ${row.guide.lastName ?? ""}` : "-",
      {
        header: t`Guide`,
      },
    ),
    ch.display({
      id: "comment",
      header: "",
      Cell: ({ row }) => (
        <UpdateAdditionalInformationModal booking={row.original} />
      ),
      enableColumnActions: false,
      size: 60,
      mantineTableHeadCellProps: {
        align: "right",
      },
      mantineTableBodyCellProps: {
        align: "right",
      },
    }),
  ];

  const defaultMRTOptions = getDefaultMRTOptions<GetBookingsItem>();

  const table = useMantineReactTable({
    data: useMemo(() => data?.data ?? [], [data?.data]),
    columns,
    ...defaultMRTOptions,
    renderTopToolbar: ({ table }) => (
      <>
        <Flex justify="space-between" p="md">
          <Group gap="xs">
            <MRT_GlobalFilterTextInput variant="default" mx={0} table={table} />
            <Switch
              label={t`Inkluder avlyste`}
              checked={includeCancelled}
              onChange={(event) =>
                setIncludeCancelled(event.currentTarget.checked)
              }
            />
          </Group>

          <Group gap="xs">
            <MRT_ToggleFiltersButton table={table} />
            <MRT_ShowHideColumnsButton table={table} />
            <MRT_ToggleFullScreenButton table={table} />
          </Group>
        </Flex>
        <Divider />
      </>
    ),
    enableFacetedValues: true,
    state: {
      isLoading,
    },
    mantinePaperProps: {
      shadow: "none",
      withBorder: false,
    },
    mantineTableProps: {
      layout: "fixed",
    },
  });

  if (!customerId) {
    return null;
  }

  return (
    <Paper>
      <Text size="sm" px="xs" pt="xs" mb="xs">
        <Trans>Bookinger</Trans>
      </Text>

      <MantineReactTable table={table} />
    </Paper>
  );
}

function ProgramCell({ program }: { program: GetBookingsItem["program"] }) {
  const { i18n } = useLingui();

  const programStringified = program?.map((program) => {
    return `${program.productNameNO ?? "-"}${program.start || program.end ? " " : ""}${
      program.start ? i18n.date(program.start, { timeStyle: "short" }) : ""
    }${program.start || program.end ? "–" : ""}${program.end ? i18n.date(program.end, { timeStyle: "short" }) : ""}`;
  });

  if (!program || program.length === 0) {
    return "-";
  }

  return (
    <HoverCard withArrow>
      <HoverCard.Target>
        <Text size="sm" truncate="end">
          {programStringified?.join(", ") ?? "-"}
        </Text>
      </HoverCard.Target>
      <HoverCard.Dropdown p="xs">
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "auto auto",
            columnGap: 8,
            rowGap: 4,
          }}
        >
          {program?.map((product) => (
            <Fragment key={product.id}>
              <Text size="xs" ta={!product.start ? "right" : "left"}>
                {product.start
                  ? i18n.date(product.start, {
                      timeStyle: "short",
                    })
                  : ""}
                {product.start || product.end ? "–" : ""}
                {product.end
                  ? i18n.date(product.end, { timeStyle: "short" })
                  : ""}
              </Text>
              <Stack gap={0}>
                <Text size="xs">{product.productNameNO}</Text>
                <Text size="xs" c="dimmed">
                  {product.comment}
                </Text>
              </Stack>
            </Fragment>
          ))}
        </div>
      </HoverCard.Dropdown>
    </HoverCard>
  );
}
